import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GraduationCap, Inbox, Video } from 'lucide-react';
import ReportIssueButton from './ReportIssueButton';
import HelpMenu, { describePath } from './HelpMenu';
import TutorialRecorderMount, { canShowTutorialRecorder } from '../tutorial/TutorialRecorderMount';
import ReportIssueModal from './ReportIssueModal';
import TestFixBanner from './TestFixBanner';
import { installConsoleBuffer } from '../../utils/issueReporter/consoleBuffer';
import { installNetworkBuffer } from '../../utils/issueReporter/networkBuffer';
import { captureScreenshot, ScreenshotResult } from '../../utils/issueReporter/screenshotCapture';
import { supportApi } from '../../utils/issueReporter/supportApi';

interface Props {
  user: { id: string; email: string; role: string; name?: string } | null;
  tenantId: string;
  tenantName?: string;
  productId: string;
}

/**
 * Single mount point for the in-app issue reporter:
 *   - Installs console + network ring buffers (idempotent; safe on re-render)
 *   - Renders the floating help/support launcher + its menu: Report a
 *     problem, Tutorials, Record a tutorial, Support tickets (the /support
 *     inbox). Same HelpMenu as platform-app, ruby-on-light skin.
 *   - Renders the Report Issue modal and the tutorial recorder panel
 *   - Renders the Test Fix banner when a `?retest=...` URL is opened
 *
 * Place near the root of the app, *inside* the authenticated layout so we
 * only ever capture data for signed-in users. The component renders nothing
 * if `user` is null.
 */
const IssueReporterRoot: React.FC<Props> = ({ user, tenantId, tenantName, productId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [recorderPanelOpen, setRecorderPanelOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSteps, setRecordingSteps] = useState(0);
  // Same gate platform-app uses: admin claim AND a browser with getDisplayMedia.
  const [canRecord, setCanRecord] = useState(false);
  useEffect(() => { setCanRecord(canShowTutorialRecorder()); }, []);
  const [open, setOpen] = useState(false);
  const [awaiting, setAwaiting] = useState(0);
  const [launcherBottom, setLauncherBottom] = useState(10);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<ScreenshotResult | null>(null);

  // Capture the page shot *before* showing the modal so the modal itself
  // isn't in the screenshot. The FAB is in the corner so it's basically
  // invisible at full-page scale — acceptable.
  const openReporter = async () => {
    setMenuOpen(false);
    if (capturing) return;
    setCapturing(true);
    try {
      const shot = await captureScreenshot();
      setScreenshot(shot);
      setOpen(true);
    } finally {
      setCapturing(false);
    }
  };

  const closeReporter = () => {
    setOpen(false);
    setScreenshot(null);
  };

  // Install buffers as early as possible.
  useEffect(() => {
    installConsoleBuffer();
    installNetworkBuffer();
  }, []);

  // Poll for awaiting-retest count (cheap call) so the button shows a badge
  // when there's something for the user to verify. Light polling — every 90s
  // and on window focus.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await supportApi.listMyIssues('awaiting_retest');
        if (!cancelled) setAwaiting(res.count || 0);
      } catch {
        // ignore — badge is best-effort
      }
    };
    refresh();
    const timer = setInterval(refresh, 90_000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id]);

  if (!user) return null;

  const ctx = describePath(location.pathname, 'Super Admin');

  return (
    <>
      <TestFixBanner />
      <ReportIssueButton
        awaitingRetestCount={awaiting}
        recording={recording && !recorderPanelOpen}
        recordingSteps={recordingSteps}
        onClick={() => setMenuOpen((o) => !o)}
        hidden={open || capturing}
        onBottomChange={setLauncherBottom}
      />

      <HelpMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        skin="light"
        contextEyebrow={ctx.eyebrow}
        contextTitle={ctx.title}
        reportBadge={awaiting}
        onReport={openReporter}
        launcherBottom={launcherBottom}
        recording={recording && !recorderPanelOpen ? { steps: recordingSteps, onOpen: () => { setMenuOpen(false); setRecorderPanelOpen(true); } } : null}
        items={[
          { key: 'tutorials', icon: GraduationCap, label: 'Tutorials', onClick: () => { setMenuOpen(false); navigate('/affiliates/certification?tab=tutorials'); } },
          ...(canRecord && !recording ? [{ key: 'record', icon: Video, label: 'Record a tutorial', onClick: () => { setMenuOpen(false); setRecorderPanelOpen(true); } }] : []),
          { key: 'support', icon: Inbox, label: 'Support tickets', onClick: () => { setMenuOpen(false); navigate('/support'); } },
        ]}
      />

      {/* Always mounted once gated in, so a recording survives the panel
          being closed — see TutorialRecorderMount. */}
      <TutorialRecorderMount
        panelOpen={recorderPanelOpen}
        onClose={() => setRecorderPanelOpen(false)}
        onRecordingChange={(isRecording, steps) => { setRecording(isRecording); setRecordingSteps(steps); }}
      />

      <ReportIssueModal
        open={open}
        onClose={closeReporter}
        user={user}
        tenantId={tenantId}
        tenantName={tenantName}
        productId={productId}
        screenshot={screenshot}
        onSubmitted={() => {
          // Refresh badge count after submission
          supportApi.listMyIssues('awaiting_retest').then((r) => setAwaiting(r.count || 0)).catch(() => {});
        }}
      />
    </>
  );
};

export default IssueReporterRoot;
